import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../utils/auth';
import { getConsent, onConsentChange } from '../utils/consent';

const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');
  // Hidden until the consent choice is made — the blocking ConsentBanner
  // (z-40, deliberately below the AuthPopup) must not be covered by this
  // launcher (z-50). Any choice, accept or decline, reveals the widget.
  const [consentDecided, setConsentDecided] = useState(() => Boolean(getConsent()));
  const consoleErrorsRef = useRef([]);

  useEffect(() => onConsentChange(() => setConsentDecided(true)), []);

  // Capture console errors
  useEffect(() => {
    const handleError = (event) => {
      const errorInfo = `${event.message} (${event.filename}:${event.lineno})`;
      consoleErrorsRef.current = [
        ...consoleErrorsRef.current.slice(-4),
        errorInfo
      ];
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!message.trim() || status === 'submitting') return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      // Backend proxy adds the userId from the JWT and forwards to the
      // feedback webhook — no email, no direct third-party call from the
      // browser. Only the path is sent, never query strings.
      await api.post('/feedback', {
        message: message.trim(),
        pageUrl: window.location.pathname,
        consoleErrors: consoleErrorsRef.current.join('\n'),
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`
      });

      setStatus('success');
      setMessage('');
    } catch (error) {
      // api.post throws on any non-2xx response, so every failure —
      // including a missing webhook config on the server — lands here.
      setStatus('error');
      setErrorMessage('Kunne ikke sende tilbakemelding. Prøv igjen.');
    }
  }, [message, status]);

  const handleReset = () => {
    setStatus('idle');
    setMessage('');
    setErrorMessage('');
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset after animation
    setTimeout(() => {
      if (status !== 'submitting') {
        handleReset();
      }
    }, 200);
  };

  if (!consentDecided) return null;

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 hover:shadow-xl z-50 ${
          isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ backgroundColor: '#798F7D' }}
        aria-label="Åpne tilbakemeldingsskjema"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="white"
          className="w-6 h-6"
        >
          <path
            fillRule="evenodd"
            d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Feedback panel */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 w-80 max-w-[calc(100vw-3rem)] bg-[#FCFCF9] rounded-lg shadow-xl z-50 overflow-hidden"
          style={{ maxHeight: 'calc(100vh - 6rem)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Tilbakemelding</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Lukk"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {status === 'success' ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="#798F7D"
                    className="w-6 h-6"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-gray-700 mb-4">Takk for tilbakemeldingen!</p>
                <button
                  onClick={handleReset}
                  className="text-sm hover:underline"
                  style={{ color: '#798F7D' }}
                >
                  Send ny
                </button>
              </div>
            ) : (
              <>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Beskriv problemet eller gi oss en tilbakemelding..."
                  className="w-full h-28 p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm"
                  style={{ '--tw-ring-color': '#798F7D' }}
                  disabled={status === 'submitting'}
                />

                {status === 'error' && (
                  <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || status === 'submitting'}
                  className="mt-3 w-full py-2 px-4 rounded-lg text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  style={{ backgroundColor: '#798F7D' }}
                >
                  {status === 'submitting' ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sender...
                    </>
                  ) : status === 'error' ? (
                    'Prøv igjen'
                  ) : (
                    'Send'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
